import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import Button from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrentStudentProfile, useStudentCourses } from '../../hooks/useLmsData';
import { useAcademySettings } from '../../hooks/useAcademySettings';
import { useBranches } from '../../hooks/useBranches';
import { ensureStudentAcademyId } from '../../utils/lmsMutations';
import {
  buildStudentQrResolverUrl,
  getBranchFromCourses,
  getStudentDisplayName,
  getStudentRegistrationDate,
} from '../../utils/studentIdentity';
import { formatStudentDate } from '../../utils/studentExperience';

const BadgeIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
    <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v9A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5v-9Zm2.2-.5 5.43 4.19a.6.6 0 0 0 .74 0L17.8 7H6.2Z" />
  </svg>
);

const IdCardField = ({ label, value, icon, compact = false }) => (
  <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
    <div className="flex items-center gap-2 text-[#8f5d69]">
      {icon ? <span className="flex h-4 w-4 items-center justify-center">{icon}</span> : null}
      <p className="text-[11px] font-medium tracking-[0.02em]">{label}</p>
    </div>
    <p
      className={`${
        compact ? 'text-[1rem]' : 'text-[1.4rem]'
      } font-medium leading-tight tracking-[-0.02em] text-[#1a1429]`}
    >
      {value || 'Not set'}
    </p>
  </div>
);

const StudentIdCard = ({
  showPrintButton = false,
  className = '',
}) => {
  const { currentUser } = useAuth();
  const { data: profileData, loading: profileLoading, refresh: refreshProfile } =
    useCurrentStudentProfile(currentUser?.uid);
  const { data: courseData, loading: courseLoading } = useStudentCourses(currentUser?.uid);
  const { academy } = useAcademySettings();
  const { byId: branchesById, bySlug: branchesBySlug, list: branchList } = useBranches();
  const [qrUrl, setQrUrl] = useState('');
  const [ensuringId, setEnsuringId] = useState(true);

  useEffect(() => {
    const ensureId = async () => {
      if (!currentUser?.uid) return;
      try {
        await ensureStudentAcademyId({ userId: currentUser.uid });
        refreshProfile();
      } catch (error) {
        console.error('Failed to ensure academy student id:', error);
      } finally {
        setEnsuringId(false);
      }
    };

    ensureId();
  }, [currentUser?.uid, refreshProfile]);

  const academyStudentId =
    profileData?.student?.academyStudentId || profileData?.user?.academyStudentId || '';
  const displayName = useMemo(
    () => getStudentDisplayName(profileData?.user, profileData?.student),
    [profileData],
  );
  const registrationDate = getStudentRegistrationDate(
    profileData?.user,
    profileData?.student,
  );
  const branch = useMemo(
    () =>
      getBranchFromCourses(
        {
          student: profileData?.student,
          enrollments: courseData?.enrollments || [],
          courses: courseData?.courses || {},
        },
        { byId: branchesById, bySlug: branchesBySlug },
      ),
    [profileData, courseData, branchesById, branchesBySlug],
  );
  const branchEntry = useMemo(() => {
    if (!branch) return null;
    return (
      branchList.find(
        (entry) => entry.name === branch || entry.slug === branch.toLowerCase(),
      ) || null
    );
  }, [branch, branchList]);
  const tvecNumber = branchEntry
    ? academy?.tvecRegistrations?.[branchEntry.id] || ''
    : '';
  const photoUrl =
    currentUser?.photoURL ||
    profileData?.user?.photoURL ||
    profileData?.student?.documents?.photoURL ||
    profileData?.student?.personalDetails?.photoURL ||
    '/images/profile.png';

  useEffect(() => {
    const buildQr = async () => {
      if (!academyStudentId) return;
      try {
        const dataUrl = await QRCode.toDataURL(
          buildStudentQrResolverUrl(academyStudentId),
          {
            width: 260,
            margin: 1,
            color: {
              dark: '#2f1730',
              light: '#ffffff',
            },
          },
        );
        setQrUrl(dataUrl);
      } catch (error) {
        console.error('Failed to generate student card QR:', error);
      }
    };

    buildQr();
  }, [academyStudentId]);

  if (profileLoading || courseLoading || ensuringId) {
    return (
      <div className="flex min-h-[18rem] items-center justify-center rounded-[30px] border border-stone-200 bg-white">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {showPrintButton ? (
        <div className="mb-4 flex justify-end print:hidden">
          <Button type="button" onClick={() => window.print()}>
            Print card
          </Button>
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[20.1rem] print:max-w-[20.1rem]">
        <div className="relative overflow-hidden rounded-[30px] border border-[#f3d8d4] bg-[#fff9fa] shadow-[0_34px_70px_-42px_rgba(83,30,52,0.46)] print:shadow-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_84%_10%,rgba(255,245,222,0.95),transparent_3%),radial-gradient(circle_at_84%_10%,rgba(255,220,228,0.82),transparent_11%),radial-gradient(circle_at_18%_35%,rgba(255,236,240,0.8),transparent_21%),radial-gradient(circle_at_72%_76%,rgba(255,224,231,0.7),transparent_18%),linear-gradient(180deg,#fff9fa_0%,#fdf0f3_57%,#a96b82_100%)]" />
          <div className="absolute bottom-[1.05rem] left-[-1.5rem] h-16 w-[128%] rotate-[-6deg] rounded-full border-t border-white/30 opacity-35" />
          <div className="absolute bottom-[0.38rem] left-[-0.8rem] h-12 w-[120%] rounded-full border-t border-[#ffd7d5]/45 opacity-45" />
          <div className="absolute bottom-0 left-0 right-0 h-[3.9rem] bg-[linear-gradient(180deg,rgba(122,61,81,0.52)_0%,rgba(91,45,64,0.94)_100%)]" />
          <div className="absolute bottom-0 left-1/2 h-[0.78rem] w-[3.9rem] -translate-x-1/2 rounded-t-full border border-[#ead0c1] bg-[linear-gradient(180deg,#fff0e7_0%,#d9b8a8_100%)]" />
          <div className="absolute left-6 top-11 h-1.5 w-1.5 rounded-full bg-white/70 shadow-[0_0_18px_rgba(255,255,255,0.8)]" />
          <div className="absolute right-7 top-9 h-12 w-12 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.92)_0%,rgba(255,231,235,0.58)_28%,transparent_62%)] opacity-90" />

          <div className="relative flex min-h-[36rem] flex-col">
            <div className="relative overflow-hidden border-b border-[#edd5db] bg-white px-5 py-4">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_28%,rgba(217,79,138,0.1),transparent_24%),radial-gradient(circle_at_84%_18%,rgba(240,176,206,0.22),transparent_26%),linear-gradient(135deg,rgba(255,255,255,0)_0%,rgba(244,226,235,0.75)_48%,rgba(255,255,255,0)_100%)]" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[linear-gradient(90deg,rgba(223,179,198,0),rgba(223,179,198,0.95),rgba(223,179,198,0))]" />
              <div className="grid grid-cols-[4.4rem_minmax(0,1fr)] items-center gap-3">
                <img
                  src={
                    academy?.logoURL ||
                    'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTZX4V40gh0oMaKesWquVOTDB_W0UcErXKHyQ&s'
                  }
                  alt={`${academy?.name || 'Brissbella'} academy crest`}
                  className="h-[4.4rem] w-[4.4rem] rounded-2xl object-cover"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = 'https://www.brissbella.com/images/academy-logo.jpg';
                  }}
                />

                <div className="relative min-w-0 text-left leading-none">
                  <p className="font-['Georgia'] text-[1.3rem] font-semibold uppercase tracking-[0.12em] text-[#2f2745] [text-shadow:0_1px_0_rgba(255,255,255,0.8)]">
                    {(academy?.shortName || academy?.name || 'Brissbella').toUpperCase()}
                  </p>
                  <p className="mt-1 text-[0.78rem] font-medium uppercase tracking-[0.3em] text-[#7c6878]">
                    {academy?.tagline || 'Hair and beauty school'}
                  </p>
                  {tvecNumber ? (
                    <p className="mt-1 text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#a25569]">
                      {tvecNumber}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="relative flex-1 px-5 pb-8 pt-6">
              <div className="grid grid-cols-[6.8rem_minmax(0,1fr)] items-center gap-4">
                <div className="rounded-full border-[3px] border-[#fff8f8] bg-[#f6dee7] p-[3px] shadow-[0_18px_32px_-22px_rgba(86,33,57,0.45)]">
                  <img
                    src={photoUrl}
                    alt={displayName}
                    className="h-[6.05rem] w-[6.05rem] rounded-full object-cover"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = '/images/profile.png';
                    }}
                  />
                </div>

                <div className="min-w-0">
                  <p className="text-[0.72rem] font-medium uppercase tracking-[0.28em] text-[#a25569]">
                    Student ID
                  </p>
                  <div className="mt-2 flex items-center gap-2 border-y border-[#f1dde3] py-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[linear-gradient(180deg,#d68aa3_0%,#b45f79_100%)] text-white shadow-[0_8px_20px_-16px_rgba(86,33,57,0.6)]">
                      <BadgeIcon />
                    </span>
                    <p className="text-[0.8rem] font-semibold tracking-[0.04em] text-[#1a1529] [overflow-wrap:anywhere]">
                      {academyStudentId || 'Not ready'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-1.5">
                <h3 className="text-[1.25rem] font-medium leading-[1.12] tracking-[-0.03em] text-[#1a152a]">
                  {displayName}
                </h3>
                <p className="text-[0.82rem] text-[#5f4d5b]">Student</p>
              </div>

              <div className="mt-6 grid grid-cols-[minmax(0,1fr)_7rem] items-end gap-4">
                <div className="space-y-3">
                  <div className="h-px bg-[#ecd7df]" />
                  <IdCardField
                    label="Registered On"
                    value={formatStudentDate(registrationDate)}
                    icon={
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                        <path d="M7 2.75a.75.75 0 0 1 1.5 0V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 20 6.75v10.5A2.75 2.75 0 0 1 17.25 20H6.75A2.75 2.75 0 0 1 4 17.25V6.75A2.75 2.75 0 0 1 6.75 4H7V2.75Zm11.5 6H5.5v8.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-8.5Z" />
                      </svg>
                    }
                    compact
                  />
                  <div className="h-px bg-[#ecd7df]" />
                  <IdCardField
                    label="Branch"
                    value={branch}
                    icon={
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                        <path d="M12 2.5 4.5 6v2.5h15V6L12 2.5ZM6 10v8h4v-5h4v5h4v-8H6Zm-1.5 9.5h15v2h-15v-2Z" />
                      </svg>
                    }
                    compact
                  />
                </div>

                <div className="w-[7rem] rounded-[20px] border border-[#e7c4b5] bg-[linear-gradient(180deg,#fffdfd_0%,#fff3ee_100%)] p-2 shadow-[0_18px_34px_-24px_rgba(84,31,55,0.28)]">
                  <div className="rounded-[14px] border border-[#f1d6d5] bg-white p-1.5 shadow-inner">
                    {qrUrl ? (
                      <img
                        src={qrUrl}
                        alt="Student QR code"
                        className="h-full w-full rounded-[8px]"
                      />
                    ) : (
                      <div className="flex h-[5.8rem] items-center justify-center text-[10px] text-stone-500">
                        Preparing QR...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-7 px-1 text-[9px] font-medium tracking-[0.04em] text-[#7e6471]">
                <div className="flex items-center justify-between gap-4 text-[9px] leading-none text-[#7f6270]">
                  <span>www.brissbella.com</span>
                  <span>info@brissbella.com</span>
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-auto flex items-center justify-center px-5 pb-5 pt-3 text-[9px] font-semibold uppercase tracking-[0.14em] text-white">
              <span>Reg No. {academyStudentId || '-'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentIdCard;
